import type {
  CityInterface,
  CountryInterface,
  SeaportInterface,
  SeaportAirportInterface,
} from '~/api/common'
import type {
  CompanyDirectoryItem,
  MembershipListItem,
} from '~/api/membershipList'

export interface MembershipListItemInterface extends MembershipListItem {
  isSelected: boolean
}

export interface MembershipListItemRouteInterface {
  vipCode: string
}

export interface CompanySearchRouteParams {
  search: CompanySearch
  membership: MembershipListItemRouteInterface[]
  memberYears: number
  advantages: any[]
  portList?: SeaportInterface[]
  certificateList?: any[]
  routeList?: any[]
  checkedCase?: boolean
  checkedCredit?: boolean
  checkedJcvf?: boolean
  checkedGoodService?: boolean
  shippingPartnerList?: any[]
  airlinePartnerList?: any[]
}

export enum CompanySearchRouteName {
  membershipListC = 'companyListC',
  membershipListP = 'companyListP',
  membershipList = 'companyList',
  membershipListAd = 'companyListAd',
  membershipListCompany = 'companyListBase',
  membershipListCompanyPage = 'companyListPage',
  membershipListCompanyCountry = 'companyListCountry',
  membershipListCompanyCountryPage = 'companyListCountryPage',
  // membershipListC = 'membershipListC',
  // membershipListP = 'membershipListP',
  // membershipList = 'membershipList',
  // membershipListAd = 'membershipListAd',
}

// export type CompanyListItem = CompanyDirectoryItem & {
//   totalYears: number // 会员总年限
//   vipListVoList: Array<{
//     vipIcon?: {
//       url?: string
//       name?: string
//     }
//   }>
// }
// export type CompanySearchType = 'location' | 'id' | 'name' | 'port'

export type CountryCityModules = 'country' | 'city'

export interface OnlyIdData {
  id: number
}

export type OnlyIdDataOrNull = OnlyIdData | null

export type CompanySearchValue = {
  0:
    | CountryInterface
    | OnlyIdData
    | SeaportInterface[]
    | OnlyIdData[]
    | string
    | null
  1?: CityInterface[] | OnlyIdData[] | null
}

// export type CompanySearch = {
//   type: CompanySearchType
//   value: CompanySearchValue
// }

export type CountryType =
  | CountryInterface
  | CountryInterface[]
  | OnlyIdDataOrNull

export type CityType =
  | CityInterface
  | CityInterface[]
  | OnlyIdData[]
  | OnlyIdDataOrNull

export type PortType = SeaportAirportInterface[] | null
export type SeaAirPortType = SeaportAirportInterface[] | null

export function isCountryInterface(obj: any): obj is CountryInterface {
  return obj && typeof obj === 'object' && 'id' in obj
}

export function isCityInterface(obj: any): obj is CityInterface {
  return obj && typeof obj === 'object' && 'id' in obj
}

export const PAGE_SIZE = 30
/** 国家/城市列表分页；过大时首屏会一次请求并渲染上百国旗 SVG */
export const COUNTRY_PAGE_SIZE = 40
export const CITY_PAGE_SIZE = 40
export const CUSTOM_TAB_TITLE: {
  [key in CompanySearchType]: string
} = {
  location: 'companySearch.search.location',
  name: 'companySearch.search.name',
  id: 'companySearch.search.id',
  port: 'companySearch.search.port',
}

export const CUSTOM_POPUP_TITLE: {
  [key in CountryCityModules | 'port']: string
} = {
  country: 'companySearch.search.selectCountry',
  city: 'companySearch.search.selectCity',
  port: 'companySearch.search.selectPort',
}

/**
 * 判别联合类型：根据搜索类型，value 对应不同的数据结构
 */
export type CompanySearch =
  | {
      type: 'location'
      /**
       * [国家, 可选城市]
       * - value[0]: CountryInterface
       * - value[1]?: CityInterface
       */
      value: [country: CountryInterface, city?: CityInterface]
    }
  | {
      type: 'name'
      /**
       * [公司名称]
       */
      value: [name: string]
    }
  | {
      type: 'id'
      /**
       * [公司 ID]
       */
      value: [id: string]
    }
  | {
      type: 'port'
      /**
       * [港口/机场列表]
       * - value[0]: SeaportAirportInterface[]
       */
      value: [ports: SeaportAirportInterface[]]
    }

/**
 * 搜索类型别名
 */
export type CompanySearchType = CompanySearch['type']

/**
 * 会员列表项接口（带选中状态）
 */
export interface MembershipListItemInterface extends MembershipListItem {
  isSelected: boolean
}

/**
 * 路由中会员代码接口
 */
export interface MembershipListItemRouteInterface {
  vipCode: string
}

/**
 * 名录排序：DEFAULT-默认排序；CREDIT-信誉排序（仅登录）；ORDER-订单排序
 */
export type CompanySortType = 'DEFAULT' | 'CREDIT' | 'ORDER'

export const parseCompanySortType = (raw: unknown): CompanySortType => {
  if (raw === 'CREDIT') return 'CREDIT'
  if (raw === 'ORDER' || raw === 'SALES') return 'ORDER'
  return 'DEFAULT'
}

/**
 * 路由参数接口
 */
export interface CompanySearchRouteParams {
  search: CompanySearch
  membership: MembershipListItemRouteInterface[]
  memberYears: number
  advantages: any[]
  portList?: SeaportInterface[]
  certificateList?: any[]
  routeList?: any[]
  checkedCase?: boolean
  checkedCredit?: boolean
  checkedJcvf?: boolean
  checkedGoodService?: boolean
  shippingPartnerList?: any[]
  airlinePartnerList?: any[]
  sortType?: CompanySortType
}

/**
 * 公司列表项类型
 */
export type CompanyListItem = CompanyDirectoryItem & {
  totalYears: number // 会员总年限
  vipListVoList: Array<{
    vipIcon?: {
      url?: string
      name?: string
    }
  }>
}
export type CompanySearchCondMap = {
  resultCount_var: number
  searchContent_var: string
  searchFunction_var: string
  searchKeyword_var: string
  searchType_var: string
  searchfilterword_var: string[]
}
// 支持的筛选类型
export type FilterType =
  | 'sort'
  | 'advantage'
  | 'memberYear'
  | 'memberShip'
  | 'certificate'
  | 'route'
  | 'case'
  | 'credit'
  | 'jcvf'
  | 'serviceHighlights'
  | 'shippingPartner'
  | 'airlinePartner'

// 单个已选筛选项（id、类型）
export interface SelectedFilterItem {
  id: number | string
  type: FilterType
}

// 当前筛选的聚合形式
export interface CurrentSelectedFilterGroup {
  type: FilterType
  ids: Array<number | string>
}

// 单个筛选项的结构（包含 UI 文本及子选项）
export interface FilterItem {
  index: number
  code: FilterType
  text: string
  children: Array<any> // 你可以根据 children 的具体结构进一步定义类型
  subTitle?: string
}
